import { useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { bigSharks, youtubeThumbAt, YT_THUMB_TIERS, driveThumb } from "../data/videos";

function SharkCard({ item, index, onOpen }) {
  const isDrive = item.provider === "drive";
  const [stage, setStage] = useState(0); // 0=static, 1..5=live tiers
  const src = isDrive
    ? item.poster || driveThumb(item.driveId)
    : stage === 0
    ? `/yt-thumbs/${item.youtubeId}.jpg`
    : youtubeThumbAt(item.youtubeId, YT_THUMB_TIERS[stage - 1]);
  const [imgOk, setImgOk] = useState(true);

  const handleError = () => {
    if (!isDrive && stage < YT_THUMB_TIERS.length) setStage((s) => s + 1);
    else setImgOk(false);
  };

  return (
    <motion.button
      data-testid={`big-shark-card-${item.id}`}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onOpen({ ...item, kind: "video" })}
      className="group block text-left"
    >
      <div className="relative aspect-video overflow-hidden rounded-xl border border-white/10 bg-[#0d0d0f] transition-colors duration-500 group-hover:border-[#CBFF3A]/55">
        {imgOk ? (
          <img
            key={src}
            src={src}
            alt={item.label}
            loading="lazy"
            onError={handleError}
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1f] via-[#0f0f12] to-black" />
        )}
        {/* Scrim */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/5 to-black/15 transition-colors duration-500 group-hover:from-black/75" />
        {/* Reticles */}
        <span className="absolute left-3 top-3 h-4 w-4 border-l border-t border-[#CBFF3A]/70" />
        <span className="absolute right-3 top-3 h-4 w-4 border-r border-t border-[#CBFF3A]/70" />
        <span className="absolute bottom-3 left-3 h-4 w-4 border-b border-l border-[#CBFF3A]/70" />
        <span className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-[#CBFF3A]/70" />
        {/* Index tag */}
        <span className="absolute left-4 top-4 rounded-full border border-[#CBFF3A]/40 bg-black/55 px-3 py-1 font-hud text-[9px] uppercase tracking-[0.2em] text-[#CBFF3A] backdrop-blur-sm">
          {String(index + 1).padStart(2, "0")} · Interview
        </span>
        {/* Center play */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-[#CBFF3A] group-hover:bg-black/60">
            <Play className="h-4 w-4 translate-x-[1px] fill-white text-white transition-colors group-hover:fill-[#CBFF3A] group-hover:text-[#CBFF3A]" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-600 leading-tight tracking-tight text-white md:text-xl">
            {item.label}
          </h3>
          {item.note && (
            <p className="mt-1 font-body text-xs text-white/50 md:text-sm">
              {item.note}
            </p>
          )}
          <p className="mt-2 font-hud text-[10px] uppercase tracking-[0.2em] text-[#CBFF3A]/75">
            {item.role}
          </p>
        </div>
        <ArrowUpRight
          className="h-5 w-5 shrink-0 text-white/35 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#CBFF3A]"
          strokeWidth={1.5}
        />
      </div>
    </motion.button>
  );
}

export default function BigSharks({ onOpen }) {
  return (
    <section
      id="big-sharks"
      data-testid="big-sharks-section"
      className="relative z-10 mx-auto max-w-[1400px] px-6 py-20 md:px-16 md:py-28"
    >
      <div className="mb-12 flex flex-col gap-3">
        <span className="font-hud text-[11px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
          / Big Sharks
        </span>
        <h2 className="font-display text-4xl font-600 tracking-tight text-[#ECEAE3] md:text-5xl">
          Long-Form <em className="not-italic text-[#CBFF3A]">Conversations</em>
        </h2>
        <p className="max-w-xl font-body text-white/55">
          Deep-dive interviews with founders, ministers and industry leaders —
          produced, scripted and edited end-to-end.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {bigSharks.map((item, i) => (
          <SharkCard key={item.id} item={item} index={i} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}
